import { WithRef } from '@/cms';
import { returnRefAndGenerateDataTracker } from '@/components/atoms/Button/utils';
import ImageStrapi from '@/components/atoms/Image/ImageStrapi';
import { useTranslation } from '@/lib/i18n/i18n.client';
import { cn } from '@/lib/utils';
import iconTrashCanSrc from '~/icons/trash-01.svg';
import { DropdownOption, InputDropdownWithImageProps } from '.';
import { SvgIcon } from '@/components/SvgIcon';

type DropdownListContentProps<T extends DropdownOption> = Pick<
  InputDropdownWithImageProps<T>,
  'options' | 'value' | 'renderOption'
> & {
  handleSelect: (value: string) => void;
};

function getIsClear<T extends DropdownOption>(option: T) {
  return option.value === '';
}

function OptionIcon<T extends DropdownOption>({ option }: { option: T }) {
  if (getIsClear(option)) return <SvgIcon src={iconTrashCanSrc} className='h-4 w-4' />;

  if (!option.icon) return null;

  return (
    <ImageStrapi
      block={option.icon}
      inner={{
        classNames: { image: 'h-8 w-8 object-contain' },
      }}
    />
  );
}

function OptionLabel<T extends DropdownOption>({
  option,
  renderOption,
  index,
}: {
  option: T;
  renderOption?: ({ option }: { option: T }, index: number) => React.ReactNode;
  index: number;
}) {
  const { texts } = useTranslation();

  if (getIsClear(option))
    return (
      <span className='text-body-m font-mono'>
        {texts.input_dropdown.clear_selection}
      </span>
    );

  if (renderOption) return renderOption({ option }, index);

  return option.label;
}
const DropdownListContent = <T extends DropdownOption>({
  options,
  value,
  handleSelect,
  renderOption,
  ref,
}: WithRef<HTMLInputElement, DropdownListContentProps<T>>) => {
  const { texts } = useTranslation();

  if (options.length === 0) {
    return <li className='p-1 px-4'>{texts.input_dropdown.clear_selection}</li>;
  }

  return options.map((option, index) => {
    return (
      <li
        key={index}
        className={cn(
          'h-[54px] mt-3 relative flex cursor-pointer items-center gap-3 px-4 hover:bg-gray-50',
          option.value === value && 'text-brand-500'
        )}
        ref={(_ref) => {
          returnRefAndGenerateDataTracker(_ref, ref);
        }}
        onClick={() => handleSelect(option.value)}
        data-tracker-context={option.value}
      >
        <div className='flex h-8 w-8 items-center justify-center'>
          <OptionIcon option={option} />
        </div>

        <OptionLabel
          option={option}
          renderOption={renderOption}
          index={index}
        />

        {getIsClear(option) && (
          <div className='absolute bottom-0 left-2 right-2 border-b border-gray-200' />
        )}
      </li>
    );
  });
};

export default DropdownListContent;
